/**
 * Typed SDK errors — strict subset of
 * `app-platform-gtwin/packages/sdk-ts/src/errors.ts`.
 *
 * Every class name here must exist in the source-of-truth file
 * (enforced by `__tests__/drift.test.ts`).
 */

export class GTwinSDKError extends Error {
  constructor(message: string) {
    super(message);
    this.name = this.constructor.name;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Server returned 404 for the requested gTwin, preset, or slot item. */
export class NotFoundError extends GTwinSDKError {}

/** Server returned 401 / 403 for the requested operation. */
export class NotAuthorizedError extends GTwinSDKError {}

/** Server rejected the projectAnchor payload (422). */
export class InvalidAnchorError extends GTwinSDKError {}

/** Server rejected a scene-slot item (422). */
export class InvalidSlotError extends GTwinSDKError {}

/** The anchor is already promoted; requires opts.force = true to override. */
export class AnchorAlreadyPromotedError extends GTwinSDKError {}

/** Terrain slot is a singleton; a second set() conflicts. */
export class SlotItemConflictError extends GTwinSDKError {}

/** Client-side CRS guard: item.crs does not match projectAnchor.horizontalCrs. */
export class CrsMismatchError extends GTwinSDKError {}

// ---------------------------------------------------------------------------
// WP-14 — typed errors for the real platform mutating surface.
//
// Mirrors `app-platform-gtwin/packages/sdk-ts/src/errors.ts`. Each error is
// thrown by the WP-14 real adapter (`sceneItemsReal.ts`) when the FastAPI
// service rejects a write with the matching status + error body shape per
// `SCENE_ITEMS_SDK_SPEC.md` §2.5.
// ---------------------------------------------------------------------------

/**
 * Server returned `400 invalid-scene-item`. The validator rejected an input
 * because its `(artifactKind, slot, layerKind, endpoints[*])` tuple does
 * not match the canonical mapping table from `SCENE_CONTRACT.md`.
 * `fieldPath` is the offending dotted path (e.g. `endpoints.cog`).
 */
export class InvalidSceneItemError extends GTwinSDKError {
  readonly fieldPath?: string;
  readonly code = 'invalid-scene-item' as const;

  constructor(message: string, fieldPath?: string) {
    super(message);
    this.fieldPath = fieldPath;
  }
}

/**
 * Server returned `409 stale-parent-version`. The mutating call carried a
 * `parentVersion` that no longer matches the persisted gTwin.
 * `currentVersion` is the version the server now has; `currentEnvelope` is
 * the freshly-loaded envelope so the caller can rebase without an extra
 * GET round-trip.
 */
export class StaleParentVersionError extends GTwinSDKError {
  readonly currentVersion?: number;
  readonly currentEnvelope?: Record<string, unknown>;
  readonly code = 'stale-parent-version' as const;

  constructor(
    message: string,
    currentVersion?: number,
    currentEnvelope?: Record<string, unknown>,
  ) {
    super(message);
    this.currentVersion = currentVersion;
    this.currentEnvelope = currentEnvelope;
  }
}

/**
 * Server returned `409 anchor-conflict`. The new SceneItem (or preset)
 * carries a `sourceCrs` that disagrees with the already-promoted
 * `projectAnchor` and the platform refuses to silently overwrite it.
 */
export class AnchorConflictError extends GTwinSDKError {
  readonly currentAnchor?: Record<string, unknown>;
  readonly attemptedCrs?: string;
  readonly code = 'anchor-conflict' as const;

  constructor(
    message: string,
    currentAnchor?: Record<string, unknown>,
    attemptedCrs?: string,
  ) {
    super(message);
    this.currentAnchor = currentAnchor;
    this.attemptedCrs = attemptedCrs;
  }
}
